import { z } from "zod";
import { USER_ROLES } from "../rbac/roles";
import { paginationSchema, sortSchema } from "./pagination";

/** Zod enum for user roles, aligned with USER_ROLES in rbac/roles.ts. */
export const userRoleSchema = z.enum(USER_ROLES);

/** Status filter on the admin user list. */
export const userStatusSchema = z.enum(["pending", "active", "deactivated"]);

/** Whitelist řazení uživatelů — router mapuje klíče na sloupce. */
export const USERS_SORT_KEYS = ["jmeno", "email", "role", "stav", "vytvoreno"] as const;
export type UsersSortKey = (typeof USERS_SORT_KEYS)[number];

/** Input for admin.users.list. */
export const listUsersSchema = paginationSchema.merge(sortSchema(USERS_SORT_KEYS, "desc")).extend({
  status: userStatusSchema.optional(),
  search: z.string().max(255).optional(),
});

/** Input for admin.users.updateRole. */
export const updateUserRoleSchema = z.object({
  userId: z.string().min(1, "userId is required").max(255),
  role: userRoleSchema,
});

/** Input for admin.users.approve / admin.users.reactivate. */
export const userIdSchema = z.object({
  userId: z.string().min(1, "userId is required").max(255),
});

/** Input for admin.users.deactivate — includes optional reason. */
export const deactivateUserSchema = z.object({
  userId: z.string().min(1, "userId is required").max(255),
  reason: z.string().max(500, "Reason must be 500 characters or less").optional(),
});

/** Input for admin.settings.setClientEmailsEnabled — runtime kill-switch klientských e-mailů. */
export const setClientEmailsEnabledSchema = z.object({
  enabled: z.boolean(),
});

/** Whitelist řazení audit logu — jediný smysluplný klíč je čas záznamu. */
export const AUDIT_SORT_KEYS = ["cas"] as const;
export type AuditSortKey = (typeof AUDIT_SORT_KEYS)[number];

/** Input for admin.audit.list. */
export const listAuditSchema = paginationSchema.merge(sortSchema(AUDIT_SORT_KEYS, "desc")).extend({
  module: z.string().max(100).optional(),
  userId: z.string().max(255).optional(),
});

/** Input for admin.mcp.createToken — název tokenu (např. „David — MacBook"). */
export const createMcpTokenSchema = z.object({
  nazev: z.string().min(1, "Název je povinný").max(100, "Název může mít nejvýše 100 znaků"),
});

/** Input for admin.mcp.revokeToken. */
export const revokeMcpTokenSchema = z.object({
  id: z.string().uuid("Neplatné ID tokenu"),
});

export type ListUsersInput = z.infer<typeof listUsersSchema>;
export type UpdateUserRoleInput = z.infer<typeof updateUserRoleSchema>;
export type UserIdInput = z.infer<typeof userIdSchema>;
export type DeactivateUserInput = z.infer<typeof deactivateUserSchema>;
export type ListAuditInput = z.infer<typeof listAuditSchema>;
export type CreateMcpTokenInput = z.infer<typeof createMcpTokenSchema>;
export type RevokeMcpTokenInput = z.infer<typeof revokeMcpTokenSchema>;
export type SetClientEmailsEnabledInput = z.infer<typeof setClientEmailsEnabledSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
